import { theme, useResponsive } from "@product/scmp-sdk";
import { useAtomValue, useSetAtom } from "jotai";
import capitalize from "lodash/capitalize";
import { useRouter } from "next/router";
import type { DetailedHTMLProps, HTMLAttributes } from "react";
import { forwardRef, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { TransitionStatus } from "react-transition-group";

import { hamburgerMenuAtom } from "~/components/hamburger-menu/atoms";
import { bookmarkAtom } from "~/components/header/header-bookmark/atoms";
import { useLoginDialogStateHelper } from "~/components/login-dialog/hooks";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { config, tracking } from "~/data";
import { accountAtom } from "~/lib/account";
import { useEdition, useEditionValue } from "~/lib/edition";
import { rosettaAtom } from "~/lib/rosetta";
import { useCurrentPageType, useRouteHandler } from "~/lib/router/hooks";
import { page as SearchPage } from "~/pages/search";

import { Container, Layer, MenuWrapper } from "./styles";

export type Props = {
  state: TransitionStatus;
};

interface ScmpMenuMainMenuDefaultContext extends HTMLElement {
  componentOnReady: () => Promise<void>;
  editionList: ScmpMenuEdition[];
  endpoints: ScmpMenuEndpoint[];
  menuConfig: ScmpMenuMenuConfig;
}

const menuConfigEndpoints = [
  {
    apiKey: config.graphql.drupal.apiKey,
    name: "d8",
    url: config.graphql.drupal.host,
  },
  {
    apiKey: config.graphql.contentService.apiKey,
    name: "cs-v2",
    url: config.graphql.contentService.host,
  },
  {
    apiKey: config.locationService.apiKey,
    name: "geo",
    url: config.locationService.url,
  },
];

export const HamburgerMenuContent = forwardRef<HTMLDivElement, Props>(({ state }, reference) => {
  const router = useRouter();
  const { isLoggedIn } = useAtomValue(accountAtom);
  const edition = useEditionValue();
  const { getOptions: getEditionOptions, update: updateEdition } = useEdition();
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const { showNewBookmarkAnimation: isShowBookmarkAlert, toggleShowNewBookmarkAnimation } =
    useAtomValue(bookmarkAtom);

  const hamburgerMenuReference = useRef<ScmpMenuMainMenuDefaultContext>(null);
  const menuWrapperReference = useRef<HTMLDivElement>(null);
  const [tabName, setTabName] = useState("");
  const setHamburgerMenuState = useSetAtom(hamburgerMenuAtom);
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const pageType = useCurrentPageType();
  const { openLoginDialog } = useLoginDialogStateHelper();

  const menuConfig = useMemo<ScmpMenuMenuConfig>(() => {
    const isShowSubscriptionInstruction =
      !asyncRosettaState?.result?.hasIpAccess &&
      asyncRosettaState?.result?.contentServiceUserInfo?.isShowRosetta &&
      !asyncRosettaState?.result?.isScmpSubscriber;
    return {
      containerMinHeight: isMobile ? "1600px" : "900px",
      edition,
      isShowBookmarkAlert,
      path: router.asPath,
      queryString: {
        module: tracking.module.HamburgerMenu,
        pageType,
      },
      showSubscriptionInstruction: isShowSubscriptionInstruction ?? false,
      theme: "mobile", // Always use mobile theme
    };
  }, [
    asyncRosettaState?.result?.hasIpAccess,
    asyncRosettaState?.result?.contentServiceUserInfo?.isShowRosetta,
    asyncRosettaState?.result?.isScmpSubscriber,
    isMobile,
    edition,
    isShowBookmarkAlert,
    router.asPath,
    pageType,
  ]);

  const closeMenu = useCallback(() => {
    setHamburgerMenuState({ isOpen: false });
  }, [setHamburgerMenuState]);

  const routePush = useRouteHandler();

  const closeClickedHandler = useCallback(() => {
    closeMenu();
    sendGATracking({
      action: "Menu/Close",
      category: "Menu",
      label: "Menu/Close",
    });
  }, [closeMenu]);

  useLayoutEffect(() => {
    const hamburgerMenuElement = hamburgerMenuReference?.current;
    if (hamburgerMenuElement) {
      hamburgerMenuElement.endpoints = menuConfigEndpoints;
      hamburgerMenuElement.menuConfig = menuConfig;
      hamburgerMenuElement.editionList = getEditionOptions().map(option => ({
        id: option.value,
        name: option.label,
        url: option.href,
      }));
      const linkClickedHandler = (event: ScmpMenuEvent) => {
        const menuItem = event?.detail?.menuItem;
        const headerName = event?.detail?.headerName;
        const { __typename = "", entityId, linkHref, text, urlAlias } = menuItem ?? {};
        if (text) {
          const name = capitalize(text).replace(/scmp/gi, "SCMP");
          const computeLabel = () => {
            if (headerName?.toLowerCase() === "highlights")
              return `Menu/Highlights/${name}` as const;
            if (headerName) {
              return `Menu/Click/${headerName}/${name}` as const;
            }
            return "";
          };
          const label = computeLabel();

          if (label) {
            sendGATracking({
              action: "Menu/Click",
              category: "Menu",
              label,
            });
          }
        }
        // LinkHref - Server route
        // UrlAlias - Client route
        if (linkHref) {
          closeMenu();
          window.location.href = linkHref;
        } else if (menuItem && urlAlias) {
          closeMenu();
          void routePush(urlAlias, {
            entityId,
            query: {
              module: tracking.module.HamburgerMenu,
              pageType,
            },
            type: __typename,
          });
        }
      };

      const buttonClickedHandler = (event: ScmpMenuEvent) => {
        const tabName = event.detail?.tab?.button?.text;
        if (tabName) {
          setTabName(tabName);
          sendGATracking({
            action: "Menu/Click",
            category: "Menu",
            label: `Menu/Click/${tabName}`,
          });
        }

        if (isMobile && menuWrapperReference) {
          const buttonOffsetTop = event.detail?.ref?.offsetTop ?? 0;

          setTimeout(() => {
            menuWrapperReference.current?.scrollTo({
              behavior: "smooth",
              left: 0,
              top: buttonOffsetTop - 85,
            });
          }, 500);
        }
      };

      const searchTextSubmittedHandler = (event: ScmpMenuEvent) => {
        const searchText = event?.detail?.searchText;
        if (searchText) {
          sendGATracking({
            action: "Menu/Click",
            category: "Menu",
            label: `Menu/Click/Search/${searchText}`,
          });
          closeMenu();
          const { pathname, query } = SearchPage.route({ value: searchText });
          void routePush(pathname, {
            query: { ...query, module: tracking.module.HamburgerMenu, pageType },
          });
        }
      };

      const subscribeClickedHandler = () => {
        sendGATracking({
          action: "Menu/Click",
          category: "Menu",
          label: "Menu/Subscribe",
        });
      };

      const toggleClickedHandler = (event: ScmpMenuEvent) => {
        sendGATracking({
          action: "Menu/Click",
          category: "Menu",
          label: `Menu/Click/${event.detail?.setion?.text ?? ""}/${
            event.detail?.isMenuOpen ? "Expand" : "Collapse"
          }`,
        });
      };

      const searchOpenClickedHandler = () => {
        sendGATracking({
          action: "Menu/Click",
          category: "Menu",
          label: "Menu/Search",
        });
      };

      const myNewsClickedHandler = () => {
        sendGATracking({
          action: "Menu/Click",
          category: "Menu",
          label: "Menu/mynews",
        });
      };

      const editionClickedHandler = (event: ScmpMenuEvent) => {
        const edition = event?.detail?.edition;
        if (edition === undefined) {
          return;
        }

        const { id, url } = edition;
        sendGATracking({
          action: "Menu/Click",
          category: "Menu",
          label: `Menu/Edition/${capitalize(id).replace(/hk/gi, "HK")}`,
        });
        updateEdition(id);

        window.location.href = url;
      };

      const bookmarkClickedHandler = () => {
        sendGATracking({
          action: "Menu/Click",
          category: "Menu",
          label: "Menu/bookmark",
        });
        toggleShowNewBookmarkAnimation(false);
        const destination = `${config.account.scmpAccountHost}/manage/bookmark`;
        if (isLoggedIn) {
          window.location.href = destination;
          return;
        }

        closeMenu();
        openLoginDialog({
          destination,
        });
      };

      const eventMap = {
        bookmarkClicked: bookmarkClickedHandler,
        buttonClicked: buttonClickedHandler,
        closeClicked: closeClickedHandler,
        editionClicked: editionClickedHandler,
        linkClicked: linkClickedHandler,
        myNewsClicked: myNewsClickedHandler,
        searchOpenClicked: searchOpenClickedHandler,
        searchTextSubmitted: searchTextSubmittedHandler,
        subscribeClicked: subscribeClickedHandler,
        toggleClicked: toggleClickedHandler,
      };

      Object.entries(eventMap).map(([eventName, eventHandler]) => {
        hamburgerMenuElement.addEventListener(eventName, eventHandler, false);
      });

      return () => {
        Object.entries(eventMap).map(([eventName, eventHandler]) => {
          hamburgerMenuElement.removeEventListener(eventName, eventHandler, false);
        });
      };
    }
  }, [
    hamburgerMenuReference,
    menuConfig,
    closeClickedHandler,
    closeMenu,
    routePush,
    pageType,
    isMobile,
    tabName,
    getEditionOptions,
    updateEdition,
    toggleShowNewBookmarkAnimation,
    isLoggedIn,
    openLoginDialog,
  ]);

  return (
    <Container $state={state} ref={reference}>
      <Layer onClick={closeClickedHandler} />
      <MenuWrapper $state={state} ref={menuWrapperReference}>
        <scmp-menu-main-menu-default-context ref={hamburgerMenuReference} />
      </MenuWrapper>
    </Container>
  );
});

HamburgerMenuContent.displayName = "HamburgerMenuContent";

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "scmp-menu-main-menu-default-context": DetailedHTMLProps<
        HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}
